import React, { useMemo, useRef, useEffect } from 'react';
import { Collapse } from '@fortress-technology-solutions/fortress-component-library/Transitions';
import BaseNavListItem from './NavListItem.Base';
import {
  manageMincTransactions,
  managePicTransactions,
  manageAffordableWaitlist,
  HOVER_DELAY,
  affordableHousing,
  unitCompliance,
} from './constants';

const MainNavListItem = ({
  subOptions,
  showNavText,
  isXs,
  open,
  setOpen,
  propertyDetails,
  flags,
  isTransitioning,
  setIsTransitioning,
  previousHeight,
  setPreviousHeight,
  isActiveMenu,
  isNavigatingDown,
  resetHeight = false,
  ...option
}) => {
  const subMenuRef = useRef(null);
  const collapseRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const useUxImprovements = flags && flags.navigationMenuUxImprovements;

  // Business logic: Determine if warning icon should be shown
  // Show warning for "Affordable Housing" parent section when property has non-compliant buildings
  const showWarningIcon = useMemo(() => {
    const { hasNonCompliantBuildings } = propertyDetails || {};
    return hasNonCompliantBuildings && option.value === affordableHousing;
  }, [propertyDetails, option.value]);

  const subOptionsList = useMemo(() => {
    const {
      propertyHasRD,
      propertyHasPH,
      canAccessManageAffordableWaitlist,
      hasNonCompliantBuildings,
    } = propertyDetails;

    const manageMincTransactionCondition = (subOption) =>
      subOption.value === manageMincTransactions &&
      (!flags.mincTransactionScreen || !propertyHasRD);

    const managePicTransactionCondition = (subOption) =>
      subOption.value === managePicTransactions && !propertyHasPH;

    const affordableWaitlistCondition = (subOption) =>
      subOption.value === manageAffordableWaitlist &&
      (!flags.affordableWaitlistScreen || !canAccessManageAffordableWaitlist);

    return subOptions
      ?.filter((subOpt) => {
        return manageMincTransactionCondition(subOpt) ||
          managePicTransactionCondition(subOpt) ||
          affordableWaitlistCondition(subOpt)
          ? false
          : subOpt;
      })
      .map((subOption) => {
        // Determine if this child item should show warning icon
        const showChildWarningIcon =
          hasNonCompliantBuildings && subOption.value === unitCompliance;

        return (
          <BaseNavListItem
            key={subOption.value}
            isSubOption={true}
            sx={{ pl: 4.5 }}
            {...subOption}
            flags={flags}
            showWarningIcon={showChildWarningIcon}
          />
        );
      });
  }, [subOptions, propertyDetails, flags]);

  // Handle transition events
  const handleTransitionStart = () => {
    if (useUxImprovements) {
      setIsTransitioning(true);
    }
  };

  // Handle hover events with delay
  const handleMouseEnter = () => {
    if (useUxImprovements) {
      if (subOptions && !isXs && !isTransitioning) {
        // Clear any existing timeout
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }

        // Set a new timeout
        hoverTimeoutRef.current = setTimeout(() => {
          setOpen();
          hoverTimeoutRef.current = null;
        }, HOVER_DELAY);
      }
    } else {
      setOpen();
    }
  };

  const handleMouseLeave = () => {
    // Clear the timeout if the user moves away before it expires
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const handleTransitionEnd = (event) => {
    if (!useUxImprovements) {
      return;
    }

    // When transition ends, store the current height for future use
    if (subMenuRef.current) {
      // Get the actual rendered height, not just scrollHeight
      const height = subMenuRef.current.getBoundingClientRect().height;

      // Only update previousHeight when a menu is fully expanded (onEntered event)
      if (event === 'entered' && open) {
        setPreviousHeight(height);
      }

      // Clear the height style when a menu is fully collapsed (onExited event)
      if (event === 'exited' && !open) {
        subMenuRef.current.style.height = '';
      }
    }
    setIsTransitioning(false);
  };

  // Clear timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    };
  }, []);

  // Reset height when resetHeight is true
  useEffect(() => {
    if (useUxImprovements && resetHeight && subMenuRef.current) {
      // Clear the height style to reset it
      subMenuRef.current.style.height = '';
    }
  }, [resetHeight, useUxImprovements]);

  // Apply initial height when menu is opening
  useEffect(() => {
    if (
      useUxImprovements &&
      open &&
      isActiveMenu &&
      collapseRef.current &&
      subMenuRef.current &&
      previousHeight > 0
    ) {
      // Get the actual height of the submenu
      const subMenuElement = subMenuRef.current;
      const actualHeight = subMenuElement.getBoundingClientRect().height;

      // Determine which height to use based on navigation direction
      let heightToUse;

      if (isNavigatingDown) {
        // When navigating top to bottom, use the greater value
        heightToUse =
          actualHeight > previousHeight ? actualHeight : previousHeight;
      } else {
        // When navigating bottom to top, just use the actual height
        heightToUse = actualHeight;
      }

      // Apply the appropriate height as a starting point
      subMenuElement.style.height = `${heightToUse}px`;

      // Force a reflow to ensure the initial height is applied
      void subMenuElement.offsetHeight;
    }
  }, [open, isActiveMenu, previousHeight, isNavigatingDown, useUxImprovements]);

  return (
    <BaseNavListItem
      {...option}
      open={open}
      setOpen={setOpen}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{ px: 1.5 }}
      isTransitioning={useUxImprovements ? isTransitioning : false}
      flags={flags}
      showWarningIcon={showWarningIcon}
    >
      {subOptions.length ? (
        <Collapse
          ref={collapseRef}
          in={open}
          timeout={'auto'}
          onEnter={handleTransitionStart}
          onEntering={handleTransitionStart}
          onEntered={() => handleTransitionEnd('entered')}
          onExit={handleTransitionStart}
          onExiting={handleTransitionStart}
          onExited={() => handleTransitionEnd('exited')}
        >
          <div ref={subMenuRef}>{subOptionsList}</div>
        </Collapse>
      ) : null}
    </BaseNavListItem>
  );
};

export default MainNavListItem;
